import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import Footer from './Footer';
import useSEO from '../hooks/useSEO';

const ManifestoPage = () => {
    useSEO({
      title: 'The Veydra Manifesto',
      description: 'See the system. Shape the future. Our vision for democratizing systems thinking through interactive decision tools.'
    });

    return (
        <div className="min-h-screen bg-white">
            <TopNav />

            {/* Main Content */}
            <main className="max-w-3xl mx-auto px-8 py-16 md:py-24">
                    
                    {/* Title */}
                    <div className="text-center mb-16">
                        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
                            The Veydra Manifesto
                        </h1>
                        <p className="text-xl text-gray-600 italic">
                            See the System. Shape the future.
                        </p>
                    </div>

                    {/* Content Sections */}
                    <div className="space-y-12">
                        
                        {/* Our Vision */}
                        <section>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Vision</h2>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                Veydra means bright vision through systems understanding — a living form of insight.
                                We bring light to complexity.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                <strong>Our mission is to democratize systems thinking</strong> by turning models into interactive decision tools. 
                                Whether used in classrooms or boardrooms, we help people see how choices ripple through complex systems—bridging the gap between data, intuition, and policy.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                So that educators can teach systems, leaders can steer wisely, 
                                and teams can see their world — whole, dynamic, alive.
                            </p>
                        </section>

                        {/* Origin */}
                        <section>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">Define: veydra</h2>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                The word "Veydra" is a coined name—a neologism born from deliberate intention. 
                                While you won't find it in any dictionary, its roots run deep through language and meaning.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                It echoes the ancient root <em>weid</em>, meaning to see and to know—the same lineage that gave us vision, wisdom, and idea. 
                                It resonates with the Sanskrit <em>√vid</em> (to know), from which we derive "Veda," meaning knowledge and understanding.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                Phonetically, it evokes "Vidra" (Latin for otter)—a creature symbolizing adaptability, intelligence, and playful exploration of dynamic environments.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                Conceptually, Veydra fuses <em>vision</em> + <em>hydra</em> → "many-headed vision"—representing multi-perspective insight into complex systems.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                <strong>To see clearly is to understand deeply.</strong>
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                <strong>To understand deeply is to act wisely.</strong>
                            </p>
                        </section>

                        {/* We Believe */}
                        <section>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">We Believe in Dynamic Understanding</h2>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                The world doesn't stand still — neither should our thinking.
                                Every decision, every policy, every delay sends ripples through a living system.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                Veydra exists to help people see those ripples before they become waves —
                                to turn complexity into clarity, uncertainty into foresight, and data into insight.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed italic">
                                We believe system dynamics is not just a method — it's a mindset: one that restores 
                                feedback, time, and interdependence to the way we think about strategy, policy, and progress.
                            </p>
                        </section>

                        {/* We Build */}
                        <section>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">We Build Tools for Seeing</h2>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                Veydra.io is a cloud-based system dynamics modeling and scenario-simulation platform 
                                designed to help educators, analysts, and decision-makers understand, test, and communicate complex systems through interactive, AI-assisted models.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                We provide an integrated environment where users can build, simulate, and visualize dynamic systems using intuitive modes like 
                                <strong> Design</strong>, <strong>Experiment</strong>, <strong>Calibrate</strong>, and <strong>Decide</strong>. 
                                We combine the scientific rigor of system dynamics with the usability of modern web applications and the intelligence of large language models.
                            </p>
                            
                            <div className="bg-gray-50 p-6 rounded-lg mb-6">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Capabilities Include:</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start">
                                        <span className="text-blue-600 mr-2">•</span>
                                        <span><strong>AI-Augmented Modeling:</strong> Natural-language assistants guide users in constructing and interpreting models</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-blue-600 mr-2">•</span>
                                        <span><strong>Scenario Simulation:</strong> Run and compare multiple "what-if" simulations for strategy, policy, or operational planning</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-blue-600 mr-2">•</span>
                                        <span><strong>Collaboration & Versioning:</strong> Host private or shared models, track versions, and share interactive simulations</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-blue-600 mr-2">•</span>
                                        <span><strong>Learning Environment:</strong> Model Playground with educational tutorials and Adaptive Decision Lab for workshops</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-blue-600 mr-2">•</span>
                                        <span><strong>Explainable Workflows:</strong> Every simulation and recommendation is transparent with visible equations, parameters, and causal relationships</span>
                                    </li>
                                </ul>
                            </div>
                            
                            <p className="text-lg text-gray-700 leading-relaxed">
                                Here, equations have context. Simulations have purpose. And insight is not a report — it's an experience.
                            </p>
                        </section>

                        {/* We Stand For */}
                        <section>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">We Stand for Clarity, Connection, and Consequence</h2>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                We reject oversimplified dashboards that hide cause and effect.
                                We reject static forecasts that ignore feedback and delay.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                We choose to model reality as it behaves — not as it is wished to be.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                Because clarity is power. Connection is intelligence. And understanding consequence is leadership.
                            </p>
                        </section>


                        {/* Closing */}
                        <section className="text-center pt-8 border-t border-gray-200">
                            <p className="text-2xl font-bold text-gray-900 mb-8">
                                See the system. Shape the future.
                            </p>
                            <div className="space-y-4 md:space-y-0 md:space-x-4 md:flex md:justify-center">
                                <a href="/contact" className="inline-block bg-gray-900 text-white text-lg font-medium px-8 py-3 rounded hover:bg-gray-800 transition duration-300">
                                    Schedule a conversation
                                </a>
                                <Link to="/solutions" className="inline-block border border-gray-300 text-gray-700 text-lg font-medium px-8 py-3 rounded hover:border-gray-400 hover:bg-gray-50 transition duration-300">
                                    Explore solutions
                                </Link>
                            </div>
                        </section>

                    </div>
            </main>

            <Footer />
        </div>
    );
};

export default ManifestoPage;